import type { ReactNode } from "react";
import { Alert16Filled, Alert16Regular, Checkmark16Regular, Flag16Regular } from "@fluentui/react-icons";

export type Variant = "success" | "warn" | "danger" | "neutral" | "brand";

export interface StatusStyle {
  variant: Variant;
  icon?: ReactNode;
}

const ok = <Checkmark16Regular aria-hidden />;
const flag = <Flag16Regular aria-hidden />;
const alert = <Alert16Regular aria-hidden />;
const alertFilled = <Alert16Filled aria-hidden />;

/** Status → variant (04-design-reference.md §6): the only way to colour a status. */
const MAP: Record<string, Record<string, StatusStyle>> = {
  outcome: {
    keep: { variant: "success", icon: ok },
    extend: { variant: "success", icon: ok },
    transfer_sponsor: { variant: "neutral" },
    exception: { variant: "neutral" },
    not_reviewed: { variant: "neutral" },
    remove_requested: { variant: "warn", icon: flag },
  },
  risk: {
    removal_requested: { variant: "warn", icon: flag },
    no_sponsor: { variant: "warn", icon: alert },
    no_expiry: { variant: "warn", icon: alert },
    no_reason: { variant: "warn", icon: alert },
    expiring_soon: { variant: "warn", icon: alert },
    expired: { variant: "danger", icon: alertFilled },
    overdue_review: { variant: "danger", icon: alertFilled },
    unknown_domain: { variant: "danger", icon: alertFilled },
    exception: { variant: "neutral" },
  },
  domain: {
    trusted: { variant: "success", icon: ok },
    known_partner: { variant: "success", icon: ok },
    unknown: { variant: "warn", icon: alert },
    personal: { variant: "danger", icon: alertFilled },
    watchlist: { variant: "danger", icon: alertFilled },
  },
  removal: {
    pending: { variant: "warn", icon: flag },
    scheduled: { variant: "warn", icon: flag },
    executing: { variant: "warn", icon: flag },
    completed: { variant: "success", icon: ok },
    failed: { variant: "danger", icon: alertFilled },
    rejected: { variant: "neutral" },
    cancelled: { variant: "neutral" },
  },
  exception: {
    pending_approval: { variant: "warn", icon: flag },
    active: { variant: "neutral" },
    expired: { variant: "danger", icon: alertFilled },
    rejected: { variant: "neutral" },
    revoked: { variant: "neutral" },
  },
  campaign: {
    in_progress: { variant: "warn", icon: flag },
    closed_incomplete: { variant: "warn", icon: flag },
    completed: { variant: "success", icon: ok },
    cancelled: { variant: "neutral" },
  },
  team: {
    active: { variant: "success", icon: ok },
    blocked_limit: { variant: "warn", icon: alert },
    deactivated: { variant: "neutral" },
    team_unavailable: { variant: "danger", icon: alertFilled },
  },
  presence: {
    detected: { variant: "neutral" },
    no_longer_detected: { variant: "neutral" },
    removal_completed: { variant: "neutral" },
  },
  invoice: {
    paid: { variant: "success", icon: ok },
    pending: { variant: "warn", icon: flag },
    failed: { variant: "danger", icon: alertFilled },
    refunded: { variant: "neutral" },
  },
  customer: {
    pending: { variant: "neutral" },
    trial: { variant: "brand" },
    active: { variant: "success", icon: ok },
    past_due: { variant: "danger", icon: alertFilled },
    churned: { variant: "neutral" },
  },
  suspension: {
    suspended: { variant: "warn", icon: flag },
    blocked: { variant: "danger", icon: alertFilled },
  },
  enrollment: {
    in_follow_up: { variant: "warn", icon: flag },
    converted: { variant: "success", icon: ok },
    opted_out: { variant: "neutral" },
    expired: { variant: "neutral" },
  },
  flagScope: {
    all: { variant: "success", icon: ok },
    trial_only: { variant: "warn", icon: flag },
    beta_opt_in: { variant: "brand" },
  },
  export: {
    queued: { variant: "warn", icon: flag },
    ready: { variant: "success", icon: ok },
    failed: { variant: "danger", icon: alertFilled },
  },
  subscription: {
    trial: { variant: "brand" },
    active: { variant: "success", icon: ok },
    past_due: { variant: "danger", icon: alertFilled },
    expired: { variant: "neutral" },
    cancelled: { variant: "neutral" },
  },
};

export type StatusDomain = keyof typeof MAP;

export function statusStyle(domain: StatusDomain, value: string): StatusStyle {
  return MAP[domain]?.[value] ?? { variant: "neutral" };
}

export const STATUS_DOMAINS = Object.keys(MAP) as StatusDomain[];
export function statusValues(domain: StatusDomain): string[] {
  return Object.keys(MAP[domain] ?? {});
}
